import { Mountable } from '../types';

import EventData, { setTarget } from './event-data';
import EventDelegator from './event-delegator';

interface EventDataConstructor<T extends Mountable> {
  new(...args: any[]): EventData<T>;
  [key: string]: any;
}

export interface ListenerConfig<API extends Mountable, DT extends EventData<API>> {
  api: API;
  eventDelegator: EventDelegator<API, DT>;
  type: string;
}

abstract class EventListener<API extends Mountable, ED extends EventData<API>, Args> {

  eventDataType: EventDataConstructor<API>;
  config?: ListenerConfig<API, ED>;

  constructor(dataType: EventDataConstructor<API>) {
    this.eventDataType = dataType;
  }

  private getConfig(): ListenerConfig<API, ED> {
    if (!this.config) throw new Error('[EventListener]: listener is not configured.');
    return this.config;
  }

  fire(event?: ED|Args): void {
    const config = this.getConfig();
    const eventDelegator = config.eventDelegator;
    const eventData = this.prepare(event);
    eventDelegator.fire(config.type, eventData);
  }

  listen(callback: (event: ED) => any): Function {
    const config = this.getConfig();

    const eventBus = config.eventDelegator.eventBus;
    return eventBus.insert(config.type, callback);
  }

  prepare(data?: ED|Args): ED {
    if (data instanceof this.eventDataType) {
      setTarget<API>(data, this.getConfig().api);
      return data;
    }

    const typedData = new this.eventDataType(data) as ED;
    setTarget<API>(typedData, this.getConfig().api);

    return typedData;
  }

}

// eslint-disable-next-line
interface GenericAPI extends Mountable { }

// eslint-disable-next-line
interface GenericEventData extends EventData<GenericAPI> { }

// eslint-disable-next-line
interface GenericEventListener extends EventListener<GenericAPI, GenericEventData, unknown> { }

function configureEventListener(instance: GenericEventListener, config: ListenerConfig<GenericAPI, GenericEventData>): void {
  instance.config = config;
}

export { EventListener as default, configureEventListener };
